import { Component, Input, OnInit } from '@angular/core';
import { OrganizationSettingsService } from './organization-settings.service';
import { AuthService } from 'src/app/core/services/auth.service';
import { CommonModule } from '@angular/common';
import { MatSliderModule } from '@angular/material/slider';
import { FormsModule } from '@angular/forms';
import {
  ORGANIZATION_SETTINGS_CONFIG,
  OrganizationSettingConfig,
} from './organization-settings.config';
import { OrganizationStateService } from 'src/app/shared/organization-state.service';
import { ActivatedRoute } from '@angular/router';

enum AlertType {
  Success = 'success',
  Error = 'error'
}

enum SettingId {
  LockAccount = 'lockAccount',
  LockAccountAttempts = 'lockAccountAttempts',
  LockAccountTime = 'lockAccountTime',
  AccountLocking = 'accountLocking',
  PasswordMinLength = 'passwordMinLength',
  SessionTimeout = 'sessiontimeout'
}

@Component({
  selector: 'app-organization-settings',
  standalone: true,
  templateUrl: './organization-settings.component.html',
  styleUrls: ['./organization-settings.component.scss'],
  imports: [CommonModule, MatSliderModule, FormsModule],
})
export class OrganizationSettingsComponent implements OnInit {
  @Input() orgId: string | null = null;
  orgName: string | null = null;
  loading = false;
  error: string | null = null;
  settingValues: { [key: string]: any } = {};
  settingEnabled: { [key: string]: boolean } = {};
  originalSettingValues: { [key: string]: any } = {};
  originalSettingEnabled: { [key: string]: boolean } = {};
  settingsConfig: OrganizationSettingConfig[] =
    ORGANIZATION_SETTINGS_CONFIG.map((setting) => {
      if (setting.id === 'lockAccount') {
        return {
          ...setting,
          attemptsDropdown: [3, 4, 5],
          timeDropdown: Array.from(
            { length: (60 - 5) / 5 + 1 },
            (_, i) => 5 + i * 5
          ),
        };
      }
      return setting;
    });
  alerts: {
    [key: string]: { type: AlertType; message: string } | null;
  } = {};
  alertTimeouts: { [key: string]: any } = {};

  constructor(
    private readonly settingsService: OrganizationSettingsService,
    private readonly authService: AuthService,
    private readonly orgState: OrganizationStateService,
    private readonly route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      const orgIdFromRoute = params.get('orgId');
      if (orgIdFromRoute) {
        this.orgId = orgIdFromRoute;
        this.subscribeOrgName();
        this.fetchAllSettings(this.orgId);
        this.initSettingEnabled();
      } else {
        this.settingsService
          .getPermissionAwareOrgId()
          .subscribe(({ orgId, showSettings }) => {
            this.orgId = orgId;
            this.subscribeOrgName();
            if (showSettings && this.orgId) {
              this.fetchAllSettings(this.orgId);
              this.initSettingEnabled();
            } else {
              this.settingValues = {};
              this.initSettingEnabled();
            }
          });
      }
    });
  }

  private subscribeOrgName() {
    this.orgState.orgResponse$
      .subscribe((org) => {
        this.orgName = org?.name || null;
      })
      .unsubscribe();
  }

  initSettingEnabled() {
    this.settingsConfig.forEach((setting) => {
      this.settingEnabled[setting.id] = setting.id === SettingId.PasswordMinLength;
    });
  }

  fetchAllSettings(orgId: string) {
    this.loading = true;
    const token = this.authService.token;
    let loaded = 0;
    this.settingsConfig.forEach((setting) => {
      this.settingsService.getOrgSetting(orgId, setting.id, token).subscribe({
        next: (data) => {
          if (setting.id === SettingId.LockAccount) {
            this.fetchLockAccountSettings(data, setting);
          } else {
            this.settingValues[setting.id] =
              data?.setting?.value !== undefined
                ? Number(data.setting.value)
                : null;
            this.originalSettingValues[setting.id] =
              this.settingValues[setting.id];
            this.originalSettingEnabled[setting.id] =
              this.settingEnabled[setting.id];
          }
          loaded++;
          if (loaded === this.settingsConfig.length) this.loading = false;
        },
        error: () => {
          this.error = `Failed to load ${setting.id}.`;
          loaded++;
          if (loaded === this.settingsConfig.length) this.loading = false;
        },
      });
    });
  }

  hasSettingChanged(settingId: SettingId | string): boolean {
    if (settingId === SettingId.LockAccount) {
      return (
        this.settingValues[SettingId.LockAccountAttempts] !==
          this.originalSettingValues[SettingId.LockAccountAttempts] ||
        this.settingValues[SettingId.LockAccountTime] !==
          this.originalSettingValues[SettingId.LockAccountTime] ||
        this.settingEnabled[settingId] !==
          this.originalSettingEnabled[settingId]
      );
    }
    return (
      this.settingValues[settingId] !== this.originalSettingValues[settingId] ||
      this.settingEnabled[settingId] !== this.originalSettingEnabled[settingId]
    );
  }

  updateSetting(settingId: SettingId | string) {
    if (!this.settingEnabled[settingId] || !this.orgId) return;
    if (settingId === SettingId.LockAccount) {
      this.lockAccountSetting(settingId);
      return;
    }
    if (settingId.toLowerCase() === SettingId.SessionTimeout) {
      this.sessionTimeoutSetting(settingId);
    }
    this.settingsService
      .updateOrgSetting(
        this.orgId,
        settingId,
        this.settingValues[settingId],
        this.authService.token
      )
      .subscribe({
        next: () => this.refreshSetting(settingId),
        error: () =>
          this.showAlert(settingId, AlertType.Error, 'Failed to update setting.'),
      });
  }

  private refreshSetting(settingId: SettingId | string) {
    this.settingsService
      .getOrgSetting(this.orgId!, settingId, this.authService.token)
      .subscribe({
        next: (data) => {
          this.settingValues[settingId] =
            data?.setting?.value !== undefined
              ? Number(data.setting.value)
              : this.settingValues[settingId];
          this.originalSettingValues[settingId] = this.settingValues[settingId];
          this.originalSettingEnabled[settingId] =
            this.settingEnabled[settingId];
          this.showAlert(settingId, AlertType.Success, 'Setting updated successfully.');
        },
        error: () => {
          this.showAlert(
            settingId,
            AlertType.Error,
            'Failed to refresh setting after update.'
          );
        },
      });
  }

  fetchLockAccountSettings(data: any, setting: any) {
    let parsed;
    if (data?.setting?.value) {
      try {
        parsed =
          typeof data.setting.value === 'string'
            ? JSON.parse(data.setting.value)
            : data.setting.value;
        this.settingValues[SettingId.LockAccountAttempts] = parsed.attempts ?? 3;
        this.settingValues[SettingId.LockAccountTime] = parsed.time ?? 5;
        this.settingEnabled[setting.id] = true;
      } catch {
        this.settingValues[SettingId.LockAccountAttempts] = 3;
        this.settingValues[SettingId.LockAccountTime] = 5;
        this.settingEnabled[setting.id] = false;
      }
    } else {
      this.settingValues[SettingId.LockAccountAttempts] = 3;
      this.settingValues[SettingId.LockAccountTime] = 5;
      this.settingEnabled[setting.id] = false;
    }
    this.originalSettingValues[SettingId.LockAccountAttempts] =
      this.settingValues[SettingId.LockAccountAttempts];
    this.originalSettingValues[SettingId.LockAccountTime] =
      this.settingValues[SettingId.LockAccountTime];
    this.originalSettingEnabled[setting.id] = this.settingEnabled[setting.id];
  }

  showAlert(settingId: SettingId | string, type: AlertType, message: string) {
    this.alerts[settingId] = { type, message };
    if (this.alertTimeouts[settingId])
      clearTimeout(this.alertTimeouts[settingId]);
    this.alertTimeouts[settingId] = setTimeout(() => {
      this.alerts[settingId] = null;
    }, 3000);
  }

  dismissAlert(settingId: SettingId | string) {
    this.alerts[settingId] = null;
    if (this.alertTimeouts[settingId])
      clearTimeout(this.alertTimeouts[settingId]);
  }

  lockAccountSetting(settingId: SettingId | string) {
    const attempts = this.settingValues[SettingId.LockAccountAttempts];
    const time = this.settingValues[SettingId.LockAccountTime];
    if (!attempts || !time) {
      this.showAlert(
        settingId,
        AlertType.Error,
        'Please enter values for failed attempts and time.'
      );
      return;
    }
    this.settingsService
      .updateOrgSetting(
        this.orgId,
        SettingId.AccountLocking,
        '',
        this.authService.token
      )
      .subscribe({
        next: () => {
          this.updateLockAccountChild(SettingId.LockAccountAttempts, attempts);
          this.updateLockAccountChild(SettingId.LockAccountTime, time);
          this.originalSettingEnabled[settingId] =
            this.settingEnabled[settingId];
        },
        error: () => {
          this.showAlert(
            SettingId.AccountLocking,
            AlertType.Error,
            'Failed to update account locking parent setting.'
          );
        },
      });
  }

  private updateLockAccountChild(childId: SettingId, value: number) {
    this.settingsService
      .updateOrgSetting(this.orgId, childId, value, this.authService.token)
      .subscribe({
        next: () => {
          this.showAlert(
            childId,
            AlertType.Success,
            `${
              childId === SettingId.LockAccountAttempts
                ? 'Failed attempts'
                : 'Time window'
            } setting updated successfully.`
          );
          this.originalSettingValues[childId] = value;
        },
        error: () => {
          this.showAlert(
            childId,
            AlertType.Error,
            `Failed to update ${
              childId === SettingId.LockAccountAttempts
                ? 'failed attempts'
                : 'time window'
            } setting.`
          );
        },
      });
  }

  sessionTimeoutSetting(settingId: SettingId | string) {
    const value = Number(this.settingValues[settingId]);
    let errorMsg = '';
    if (isNaN(value)) errorMsg = 'Session timeout must be a number.';
    else if (!Number.isInteger(value))
      errorMsg = 'Session timeout must be an integer.';
    else if (value < 15 || value > 360)
      errorMsg = 'Session timeout must be between 15 and 360 minutes.';
    if (errorMsg) this.showAlert(settingId, AlertType.Error, errorMsg);
  }

  clampSettingValue(setting: any, event: any, inputRef: HTMLInputElement) {
    let value = event.target.value === '' ? '' : +event.target.value;
    if (value === '') {
      this.settingValues[setting.id] = '';
      inputRef.value = '';
      return;
    }
    if (value < setting.min) value = setting.min;
    if (value > setting.max) value = setting.max;
    this.settingValues[setting.id] = value;
    inputRef.value = String(value);
  }
}